
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface MissionField {
  id: string;
  number: string;
  location: string;
  title: string;
  icon: string;
  image_url: string;
  video_url: string | null;
  prayer: string | null;
  details: string[] | null;
  display_order: number;
}

export default function MissionFields() {
  const [fields, setFields] = useState<MissionField[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      const { data, error } = await supabase
        .from('mission_fields')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setFields(data || []);
    } catch (error) {
      console.error('Error fetching mission fields:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="mission-fields" className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-base sm:text-lg text-gray-600">로딩 중...</div>
        </div>
      </section>
    );
  }

  if (fields.length === 0) {
    return (
      <section id="mission-fields" className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            KBM 후원 사역 현장
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-teal-600 font-semibold mb-6 sm:mb-8">Mission Fields</p>
          <p className="text-sm sm:text-base text-gray-600">등록된 사역 현장이 없습니다</p>
        </div>
      </section>
    );
  }

  return (
    <section id="mission-fields" className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            KBM 후원 사역 현장
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-teal-600 font-semibold">Mission Fields</p>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 mt-3 sm:mt-4 max-w-3xl mx-auto leading-relaxed">
            여러분의 후원으로 전 세계 곳곳에서 하나님 나라가 확장되고 있습니다.
          </p>
        </div>

        {/* Mission Fields Grid */}
        <div className="space-y-6 sm:space-y-8 md:space-y-12">
          {fields.map((field) => (
            <div 
              key={field.id}
              className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Image or Video */}
                <div className="relative h-48 sm:h-64 md:h-80 lg:h-auto w-full min-h-[200px] sm:min-h-[280px] lg:min-h-[320px]">
                  {field.video_url ? (
                    <video 
                      src={field.video_url}
                      controls
                      poster={field.image_url}
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover"
                    >
                      브라우저가 동영상을 지원하지 않습니다.
                    </video>
                  ) : (
                    <img 
                      src={field.image_url}
                      alt={`${field.location} ${field.title}`}
                      className="w-full h-full object-cover object-top"
                    />
                  )}
                  <div className="absolute top-3 sm:top-4 md:top-6 left-3 sm:left-4 md:left-6 bg-teal-600 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full font-bold text-xs sm:text-sm">
                    {field.number}
                  </div>
                  {field.video_url && (
                    <div className="absolute top-3 sm:top-4 md:top-6 right-3 sm:right-4 md:right-6 bg-black/70 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs flex items-center gap-1">
                      <i className="ri-video-line"></i>
                      <span>동영상</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5 sm:p-6 md:p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-teal-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${field.icon} text-lg sm:text-xl md:text-2xl text-teal-600`}></i>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-bold text-teal-600 mb-0.5 sm:mb-1">
                        {field.location}
                      </div>
                      <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                        {field.title}
                      </h3>
                    </div>
                  </div>

                  {/* Details or Prayer */}
                  {field.details ? (
                    <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                      {field.details.map((detail, detailIndex) => (
                        <div 
                          key={detailIndex}
                          className="flex items-start gap-2 sm:gap-3"
                        >
                          <i className="ri-checkbox-circle-fill text-teal-600 text-sm sm:text-base md:text-lg mt-0.5 flex-shrink-0"></i>
                          <p className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed">
                            {detail}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : field.prayer ? (
                    <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border-l-4 border-teal-600 mb-4 sm:mb-6">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <i className="ri-hand-heart-line text-lg sm:text-xl md:text-2xl text-teal-600 mt-0.5 flex-shrink-0"></i>
                        <div>
                          <h4 className="text-xs sm:text-sm font-bold text-teal-700 mb-1 sm:mb-2 uppercase tracking-wide">
                            기도 제목
                          </h4>
                          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed italic">
                            {field.prayer}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* CTA Button */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <a 
                      href="#support-application"
                      className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 bg-teal-600 text-white font-semibold rounded-lg sm:rounded-xl hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-xs sm:text-sm md:text-base"
                    >
                      <i className="ri-heart-line text-base sm:text-lg"></i>
                      <span>후원하기</span>
                    </a>
                    <button 
                      className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 bg-white text-teal-600 font-semibold rounded-lg sm:rounded-xl border-2 border-teal-600 hover:bg-teal-50 transition-all whitespace-nowrap cursor-pointer text-xs sm:text-sm md:text-base"
                    >
                      <i className="ri-share-line text-base sm:text-lg"></i>
                      <span>공유하기</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 sm:mt-12 md:mt-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 text-center text-white">
          <i className="ri-global-line text-3xl sm:text-4xl md:text-5xl mb-3 sm:mb-4 opacity-90"></i>
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            함께 만들어가는 하나님 나라
          </h3>
          <p className="text-xs sm:text-sm md:text-base lg:text-lg opacity-95 max-w-3xl mx-auto leading-relaxed mb-4 sm:mb-6">
            여러분의 기도와 후원이 이 모든 사역 현장에서 
            영혼을 구원하고 생명을 살리는 귀한 열매를 맺고 있습니다.
          </p>
          <a 
            href="#support-application"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white text-teal-600 font-bold rounded-lg sm:rounded-xl hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer text-sm sm:text-base"
          >
            <i className="ri-heart-fill text-lg sm:text-xl"></i>
            <span>지금 후원 시작하기</span>
          </a>
        </div>
      </div>
    </section>
  );
}
